import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError, catchError } from 'rxjs';
import Swal from 'sweetalert2';
import { SelectedInstituteService } from '@shared/service/selected-institute.service';
import { SelectedLanguageService } from '@shared/service/selected-language.service';
import { AuthenticationService } from '@service/admin-console/authentication/authentication.service';
import { NavigationService } from '@core/services/navigation.service';
import { AppVersionService } from '@core/services/app-version.service';
import { TranslateService } from '@ngx-translate/core';
import { HEADER_CONSTANTS } from '@shared/constants/header.constants';
import { ADMIN_PORTAL_COMMONS } from '@shared/constants/admin-portal-common.constants';
import { isForbiddenInternalEmployeeRequest } from '@core/utils/customer-error.utils';
import { environment } from '@env/environment';
import { shouldBypassInterceptor } from './interceptor.utils';
import { ToggleMode } from '@core/enums/toggle-mode.enum';
import { APP_ROUTES } from '@core/constants/app-routes.const';
import { AuthenticationResponseDto } from '@service/admin-console/authentication/model/AuthenticationResponseDto';
import { InstituteDto } from '@shared/constants/institute.constants';
import { Language } from '@shared/models/language.model';

export const digitalHubInterceptor: HttpInterceptorFn = (request, next) => {
  if (shouldBypassInterceptor(request)) {
    return next(request);
  }

  const selectedInstituteService = inject(SelectedInstituteService);
  const selectedLanguageService = inject(SelectedLanguageService);
  const authService = inject(AuthenticationService);
  const appVersionService = inject(AppVersionService);
  const router = inject(Router);
  const navigationService = inject(NavigationService);
  const translateService = inject(TranslateService);

  const selectedInstitute = selectedInstituteService.getSelectedInstitute();
  const selectedLanguage = selectedLanguageService.getStoredLanguage();
  const authData = authService.getSessionData();
  const employeeEntity = selectedInstituteService.mapInstituteForEmployeeEntity(
    selectedInstitute.entity
  );

  const headers = buildRequestHeaders(
    selectedLanguage,
    selectedInstitute,
    employeeEntity,
    authData,
    appVersionService
  );

  const modifiedRequest = request.clone({
    setHeaders: headers,
    url:
      request.url.startsWith('http') ||
      request.url.includes('127.0.0.1') ||
      request.url.startsWith('/assets/')
        ? request.url
        : environment.baseUrl + request.url,
  });

  return next(modifiedRequest).pipe(
    catchError((error: any) => {
      handleHttpError(error, router, navigationService, translateService);
      return throwError(() => error);
    })
  );
};

function buildRequestHeaders(
  selectedLanguage: Language,
  selectedInstitute: InstituteDto,
  employeeEntity: string,
  authData: AuthenticationResponseDto | null,
  appVersionService: AppVersionService
): Record<string, string> {
  const headers: Record<string, string> = {
    [HEADER_CONSTANTS.LANGUAGE]: selectedLanguage.value,
    [HEADER_CONSTANTS.BUSINESS_CHANNEL]: 'DIGITAL',
    [HEADER_CONSTANTS.BUSINESS_SUB_CHANNEL]: 'INTERNETBANKING',
    [HEADER_CONSTANTS.APP_ID]: 'admin-console',
  };

  headers[HEADER_CONSTANTS.EMPLOYEE_ENTITY] = employeeEntity;
  headers[HEADER_CONSTANTS.EMPLOYEE_NUMBER] = authData?.username || 'unknown';
  headers[HEADER_CONSTANTS.CORRELATION_ID] =
    `admin-portal-${authData?.username || 'unknown'}-${crypto.randomUUID()}`;
  headers[HEADER_CONSTANTS.APP_VERSION] =
    appVersionService.appVersion() ?? '0.0.0';
  headers[HEADER_CONSTANTS.BANK_IDENTIFICATION] = selectedInstitute.entity;

  return headers;
}

function handleHttpError(
  error: HttpErrorResponse,
  router: Router,
  navigationService: NavigationService,
  translateService: TranslateService
): void {
  if (!(error instanceof HttpErrorResponse)) return;

  if (isForbiddenInternalEmployeeRequest(error)) {
    handleForbiddenInternalEmployeeError(router, translateService);
    return;
  }

  const bawagErrorsHeader = error.headers.get('x-bawagpsk-errors');
  if (bawagErrorsHeader) {
    handleAllegroAccessError(bawagErrorsHeader, router);
    return;
  }

  if (navigationService.activeToggle() === ToggleMode.Admin) {
    handleAdminConsoleErrors(error);
    return;
  }
}

function handleAllegroAccessError(
  bawagHeader: string | null,
  router: Router
): void {
  if (!bawagHeader) return;

  try {
    const bawagErrors: Array<{ code: string; text: string }> =
      JSON.parse(bawagHeader);

    bawagErrors.forEach(errObj => {
      if (errObj.text.includes('Allegro-Access-Level-Violation')) {
        Swal.fire({
          text:
            errObj?.text || 'Unexpected error, contact system administrator!',
          icon: 'error',
          confirmButtonColor: ADMIN_PORTAL_COMMONS.THEME_CONFIRM_BUTTON_COLOR,
          confirmButtonText: 'Back To Customer Overview',
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          let route = router.routerState.root;
          let entity = '';
          let customerId = '';

          while (route.firstChild) {
            route = route.firstChild;
            if (route.snapshot.params['entity']) {
              entity = route.snapshot.params['entity'];
            }

            if (route.snapshot.params['customerId']) {
              customerId = route.snapshot.params['customerId'];
            }
          }

          const redirectUrl =
            entity && customerId
              ? `/afe-customer/entity/${entity}/customer/${customerId}`
              : '/afe-customer';

          router.navigate([redirectUrl]);
        });
      }
    });
  } catch (parseErr) {
    console.warn('Failed to parse x-bawagpsk-errors header:', parseErr);
  }
}

function handleAdminConsoleErrors(error: HttpErrorResponse): void {
  if (error.status === 500) {
    Swal.fire({
      text: 'Internal server error, please try again later.',
      icon: 'warning',
      confirmButtonColor: ADMIN_PORTAL_COMMONS.THEME_CONFIRM_BUTTON_COLOR,
    });
  }
}

function handleForbiddenInternalEmployeeError(
  router: Router,
  translateService: TranslateService
): void {
  translateService
    .get([
      'errors.forbiddenInternalEmployee.title',
      'errors.forbiddenInternalEmployee.text',
    ])
    .subscribe(translations => {
      void Swal.fire({
        title: translations['errors.forbiddenInternalEmployee.title'],
        text: translations['errors.forbiddenInternalEmployee.text'],
        icon: 'error',
        confirmButtonColor: ADMIN_PORTAL_COMMONS.THEME_CONFIRM_BUTTON_COLOR,
      });

      router.navigate([`/${APP_ROUTES.AFE_CUSTOMERS}`]);
    });
}
